import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { fetchAitmTrustedDomains, addAitmTrustedDomain, updateAitmTrustedDomain, removeAitmTrustedDomain } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Pagination from './Pagination';

function formatDate(dateStr) {
  if (!dateStr) return '\u2014';
  try {
    return format(parseISO(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
}

export default function AitmTrustedDomainsManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');

  const [domain, setDomain] = useState('');
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Inline edit (notes only — domain itself is the row identity)
  const [editingId, setEditingId] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: Domain, Notes, Added By, Date, Actions
  const [colWidths, setColWidths] = useState([260, 360, 200, 130, 100]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAitmTrustedDomains();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load AiTM trusted domains');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!filterQuery.trim()) return rows;
    const q = filterQuery.toLowerCase();
    return rows.filter(
      (r) =>
        (r.domain && r.domain.toLowerCase().includes(q)) ||
        (r.notes && r.notes.toLowerCase().includes(q)) ||
        (r.addedBy && r.addedBy.toLowerCase().includes(q))
    );
  }, [rows, filterQuery]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = filtered.slice(startIdx, startIdx + pageSize);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!domain.trim()) return;

    setAdding(true);
    setAddError(null);
    try {
      const created = await addAitmTrustedDomain({
        domain: domain.trim().toLowerCase(),
        notes: notes.trim(),
      });
      setRows((prev) => [created, ...prev]);
      setDomain('');
      setNotes('');
    } catch (err) {
      setAddError(err.message || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (d) => {
    setRemoving(true);
    try {
      await removeAitmTrustedDomain(d);
      setRows((prev) => prev.filter((item) => item.domain !== d));
      setConfirmRemove(null);
    } catch (err) {
      setError(err.message || 'Failed to remove domain');
    } finally {
      setRemoving(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(row.domain);
    setEditNotes(row.notes || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditNotes('');
    setEditError(null);
  };

  const handleSaveEdit = async (row) => {
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await updateAitmTrustedDomain(row.domain, { notes: editNotes.trim() });
      setRows((prev) => prev.map((item) => (item.domain === row.domain ? { ...item, ...updated } : item)));
      cancelEdit();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheckIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">AiTM Trusted Domains (global)</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Click-host substring allowlist applied to <span className="font-mono text-gray-400">rule_aitm_url_click_to_new_ip_signin</span> for every monitored tenant. A click URL whose host matches any entry suppresses the alert. Use sparingly: a substring match bypasses the (host, app) safety guard. Email-tracker redirectors (SafeLinks, smartsheet, paylocity) should NOT be added — they wrap arbitrary destinations and would blind the rule.
        </p>
      </div>

      {/* Add Form (admin only) */}
      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">Add Trusted Domain</h2>
          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Domain (e.g. keepersecurity.com)"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                className="input-field font-mono text-xs"
                required
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                placeholder="Notes (justification — recommended)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="input-field"
              />
            </div>
            <button
              type="submit"
              disabled={adding || !domain.trim()}
              className="btn-primary flex items-center gap-2 whitespace-nowrap"
            >
              {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
              Add
            </button>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add or remove entries.</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {/* Edit error banner */}
      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      {/* Filter and Count */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Filter by domain, notes, added by..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <span className="text-xs text-gray-500">
          {filtered.length} domain{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {['Domain', 'Notes', 'Added By', 'Date', 'Actions'].map((label, colIdx) => (
                  <th
                    key={label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    className={`${colIdx === 4 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{label}</span>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, colIdx)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading trusted domains...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <ShieldCheckIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No trusted domains found</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery
                        ? 'Try adjusting your search query.'
                        : isAdmin ? 'Add a domain above to get started.' : 'No entries yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const isEditing = editingId === r.domain;
                return (
                <tr key={r.domain} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-200 truncate overflow-hidden" title={r.domain}>
                    {r.domain}
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.notes || ''}>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        className="input-field w-full text-xs py-1"
                        autoFocus
                      />
                    ) : (
                      r.notes || '\u2014'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.addedBy || ''}>
                    {r.addedBy || '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">
                    {formatDate(r.addedUtc)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'\u2014'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleSaveEdit(r)}
                          disabled={savingEdit}
                          className="text-gray-600 hover:text-green-400 transition-colors p-1"
                          title="Save"
                        >
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={savingEdit}
                          className="text-gray-600 hover:text-gray-300 transition-colors p-1"
                          title="Cancel"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === r.domain ? (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleRemove(r.domain)}
                          disabled={removing}
                          className="btn-danger px-2 py-1 text-xs"
                        >
                          {removing ? 'Removing...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmRemove(null)}
                          className="btn-secondary px-2 py-1 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => startEdit(r)}
                          className="text-gray-600 hover:text-blue-400 transition-colors p-1"
                          title="Edit notes"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setConfirmRemove(r.domain)}
                          className="text-gray-600 hover:text-red-400 transition-colors p-1"
                          title="Remove domain"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
